import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { bamsShortsApi as api } from 'api/bamsShorts';
import { BAMS_SHORTS_QUERY_KEY } from 'shared/hooks/opsVisibility/bamsShortsQueryKeys';
import { useBamsShortsDayLineQuery } from 'shared/hooks/opsVisibility/useBamsShortsDayLineQuery';
import { useBamsShortsFeedQuery } from 'shared/hooks/opsVisibility/useBamsShortsFeedQuery';
import { useBamsShortsInventoryQuery } from 'shared/hooks/opsVisibility/useBamsShortsInventoryQuery';
import { useBamsShortsLiveQuery } from 'shared/hooks/opsVisibility/useBamsShortsLiveQuery';
import { useBamsShortsSkuDetailQuery } from 'shared/hooks/opsVisibility/useBamsShortsSkuDetailQuery';

import { trendSvg } from './bamsShortsCharts';
import {
    categorise,
    CATS,
    downloadText,
    FACID,
    fmt,
    isoWeekYear,
    mapFeedRow,
    mapSkuRow,
    roll,
} from './bamsShortsUtils';
import { DayLineView } from './DayLineView';
import { LiveView } from './LiveView';
import { WeekView } from './WeekView';

import './bamsShorts.css';

export function BamsShorts() {
    // UI-only state — the data itself comes from React Query below.
    const [brands, setBrands] = useState(new Set());
    const [selWeekKey, setSelWeekKey] = useState(null);
    const [dark, setDark] = useState(
        typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches,
    );
    const [hidden, setHidden] = useState(new Set());
    const [lookupQ, setLookupQ] = useState('');
    const [lookupSel, setLookupSel] = useState(null);
    const [locOpen, setLocOpen] = useState(null); // 'facility|sku'
    const [locData, setLocData] = useState({}); // key -> {loading, rows, error}

    const [tab, setTab] = useState('week'); // 'week' | 'day' | 'live'
    const [daySite, setDaySite] = useState(null); // 'brand|facility'
    const [openDay, setOpenDay] = useState(null); // pack date string
    const [liveWin, setLiveWin] = useState('24'); // '1' | '4' | '24'

    const trendRef = useRef(null);
    const queryClient = useQueryClient();

    // ISO pack weeks to load — the current week and the one before it. Held stable for
    // the session so the query keys don't churn on every render.
    const { cur, prv } = useMemo(() => {
        const now = new Date();
        return { cur: isoWeekYear(now), prv: isoWeekYear(new Date(now.getTime() - 7 * 864e5)) };
    }, []);

    /* ---------- data: React Query (cache / cancellation / standard errors) ---------- */
    const feedQuery = useBamsShortsFeedQuery({ cur, prv });
    const skuQuery = useBamsShortsSkuDetailQuery({ cur, prv });

    const all = useMemo(() => (feedQuery.data ? feedQuery.data.map(mapFeedRow) : null), [feedQuery.data]);
    const skus = useMemo(() => (skuQuery.data ? skuQuery.data.map(mapSkuRow) : null), [skuQuery.data]);

    // Inventory is a dependent query: it needs the shorted SKUs from the SKU detail.
    const invSkus = useMemo(() => (skus ? [...new Set(skus.map((r) => r.sku))].filter(Boolean).sort() : []), [skus]);
    const invQuery = useBamsShortsInventoryQuery({ skus: invSkus });
    const inv = useMemo(() => {
        if (!skus) return null;
        // Hold off until the dependent inventory query resolves so the chase list
        // doesn't flash without its inventory columns.
        if (invSkus.length && invQuery.data === undefined) return null;
        const map = new Map();
        (invQuery.data || []).forEach((r) => {
            // Keyed by brand too: inventory is scoped per company source, so a SKU can
            // have separate BA and MS on-hand that must not be conflated.
            map.set(`${r.brand}|${r.facility}|${String(r.externalSku)}`, {
                ats: Number(r.atsUnits) || 0,
                cases: Number(r.atsCases) || 0,
                qc: Number(r.qcHoldUnits) || 0,
                unusable: Number(r.atsUnusableUnits) || 0,
                state: String(r.inventoryState || ''),
            });
        });
        return map;
    }, [skus, invSkus, invQuery.data]);

    // Day & line and Live are lazy: their queries only run once their tab is opened.
    const dayQuery = useBamsShortsDayLineQuery({ cur, prv, enabled: tab === 'day' });
    const days = dayQuery.data?.days ?? null;
    const dsku = dayQuery.data?.dsku ?? null;
    const dayBusy = dayQuery.isFetching;
    const dayErr = dayQuery.isError ? dayQuery.error?.message || 'Could not load day & line detail.' : '';

    const liveQuery = useBamsShortsLiveQuery({ enabled: tab === 'live' });
    const live = liveQuery.data ?? null;
    const liveBusy = liveQuery.isFetching;
    const liveErr = liveQuery.isError ? liveQuery.error?.message || 'Could not load the live view.' : '';
    const liveAt = liveQuery.data !== undefined && liveQuery.dataUpdatedAt ? new Date(liveQuery.dataUpdatedAt) : null;

    // Aggregate loading / error / freshness for the week-view header.
    const busy = feedQuery.isFetching || skuQuery.isFetching || invQuery.isFetching;
    const err = feedQuery.error?.message || skuQuery.error?.message || invQuery.error?.message || '';
    const status = !busy
        ? ''
        : feedQuery.isFetching
          ? 'Querying the warehouse…'
          : skuQuery.isFetching
            ? 'Loading SKU detail…'
            : 'Loading live inventory…';
    const pulledAt = feedQuery.data !== undefined && feedQuery.dataUpdatedAt ? new Date(feedQuery.dataUpdatedAt) : null;

    // Pick a sensible default week once the feed arrives, but preserve the user's current
    // week / brand selection across refetches so a refresh keeps their place.
    useEffect(() => {
        if (!all) return;
        const weekKeys = [...new Set(all.map((r) => r.year * 100 + r.week))].sort((a, b) => b - a);
        const packedByWeek = (k) => all.filter((r) => r.year * 100 + r.week === k).reduce((s, r) => s + r.packed, 0);
        const defaultWeek = weekKeys.find((k) => packedByWeek(k) > 0) || weekKeys[0] || null;
        setSelWeekKey((prev) => (prev != null && weekKeys.includes(prev) ? prev : defaultWeek));
        setBrands((prev) => {
            if (!prev.size) return prev;
            const present = new Set(all.map((r) => r.brand));
            return new Set([...prev].filter((b) => present.has(b)));
        });
    }, [all]);

    // Manual refresh re-pulls whatever is currently active (feed / sku / inventory, plus
    // the day or live tab when open). React Query handles dedupe and cancellation. The
    // per-row location drill-down is cached in local state outside React Query, so clear
    // it too — otherwise reopened rows would show stale locations after an inventory pull.
    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: [BAMS_SHORTS_QUERY_KEY] });
        setLocData({});
        setLocOpen(null);
    };

    const switchTab = (which) => setTab(which);
    const refetchLive = () => liveQuery.refetch();

    /* ---------- derived data ---------- */
    const weeks = useMemo(() => {
        if (!all) return [];
        return [...new Set(all.map((r) => r.year * 100 + r.week))].sort((a, b) => b - a);
    }, [all]);

    const CUR = useMemo(
        () => (all && selWeekKey ? all.filter((r) => r.year * 100 + r.week === selWeekKey) : []),
        [all, selWeekKey],
    );
    const priorKey = useMemo(() => weeks.find((k) => k < selWeekKey) ?? null, [weeks, selWeekKey]);
    const PRV = useMemo(
        () => (all && priorKey ? all.filter((r) => r.year * 100 + r.week === priorKey) : null),
        [all, priorKey],
    );

    const curRolled = useMemo(() => roll(CUR, brands), [CUR, brands]);
    const prvRolled = useMemo(() => (PRV ? roll(PRV, brands) : null), [PRV, brands]);

    const brandList = useMemo(() => [...new Set(CUR.map((r) => r.brand))].sort(), [CUR]);

    const totals = useMemo(() => {
        const tp = curRolled.reduce((s, e) => s + e.packed, 0);
        const ts = curRolled.reduce((s, e) => s + e.short_ord, 0);
        const tl = curRolled.reduce((s, e) => s + e.lines, 0);
        return { tp, ts, tl };
    }, [curRolled]);

    const curWeek = CUR.length ? CUR[0].week : null;
    const curYear = CUR.length ? CUR[0].year : null;

    const trendHtml = useMemo(() => {
        const trendDays = [...new Set(CUR.map((r) => r.pd))].sort();
        const series = curRolled
            .filter((e) => e.short_ord > 0)
            .map((e) => ({
                name: `${e.facility} ${e.brand.slice(0, 2).toUpperCase()}`,
                pts: trendDays.map((d) => {
                    const rr = CUR.filter((r) => r.pd === d && r.facility === e.facility && r.brand === e.brand);
                    if (!rr.length) return null;
                    const p = rr.reduce((s, x) => s + x.packed, 0);
                    const l = rr.reduce((s, x) => s + x.lines, 0);
                    return p ? l / p : null;
                }),
            }));
        return trendSvg(
            series,
            trendDays.map((d) => d.slice(5)),
            hidden,
        );
    }, [CUR, curRolled, hidden]);

    const onTrendClick = (e) => {
        const btn = e.target.closest('.tlkey');
        if (!btn) return;
        const name = btn.dataset.series;
        setHidden((prev) => {
            const next = new Set(prev);
            if (name === '__all__') next.clear();
            else if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    /* ---------- brand chip toggle ---------- */
    const toggleBrand = (b) => {
        setBrands((prev) => {
            let next = new Set(prev);
            if (!next.size) brandList.forEach((x) => next.add(x));
            if (next.has(b)) next.delete(b);
            else next.add(b);
            if (!next.size) next = new Set(); // empty means "all"
            return next;
        });
    };

    /* ---------- chase list model ---------- */
    const chase = useMemo(() => {
        if (!skus || !CUR.length) return null;
        const wk = curWeek;
        const prevWk = PRV && PRV.length ? PRV[0].week : null;
        const inWk = skus.filter((r) => r.week === wk && (!brands.size || brands.has(r.brand)));
        if (!inWk.length) return null;

        const denom = {};
        CUR.forEach((r) => {
            const k = `${r.brand}|${r.facility}`;
            denom[k] = (denom[k] || 0) + r.short_ord;
        });
        const pdenom = {};
        (PRV || []).forEach((r) => {
            const k = `${r.brand}|${r.facility}`;
            pdenom[k] = (pdenom[k] || 0) + r.short_ord;
        });
        const prevMap = new Map();
        if (prevWk != null)
            skus.filter((r) => r.week === prevWk).forEach((r) =>
                prevMap.set(`${r.brand}|${r.facility}|${r.sku}`, r.orders),
            );

        const groups = {};
        inWk.forEach((r) => {
            const k = `${r.brand}|${r.facility}`;
            (groups[k] = groups[k] || []).push(r);
        });
        const fcCount = new Map();
        const built = Object.entries(groups)
            .map(([k, rows]) => {
                const d = denom[k] || 1;
                const list = rows
                    .map((r) => {
                        const share = r.orders / d;
                        const cat = CATS[r.cat];
                        const p = prevMap.get(`${k}|${r.sku}`);
                        const pd = pdenom[k];
                        const pshare = p != null && pd ? p / pd : null;
                        return {
                            ...r,
                            share,
                            score: share * cat.w,
                            catO: cat,
                            trend: pshare === null ? null : share - pshare,
                        };
                    })
                    .sort((a, b) => b.score - a.score)
                    .slice(0, 10);
                list.forEach((r) => fcCount.set(r.sku, (fcCount.get(r.sku) || 0) + 1));
                return { k, rows: list, d };
            })
            .sort((a, b) => b.d - a.d);

        const netmin = Math.max(2, Math.ceil(Object.keys(groups).length * 0.4));
        const network = [...fcCount.entries()]
            .filter(([, c]) => c >= netmin)
            .map(([sku, c]) => {
                const any = inWk.find((r) => r.sku === sku);
                return {
                    sku,
                    name: any ? any.name : sku,
                    n: c,
                    orders: inWk.filter((r) => r.sku === sku).reduce((s, r) => s + r.orders, 0),
                };
            })
            .sort((a, b) => b.orders - a.orders)
            .slice(0, 6);

        return { built, fcCount, netmin, network, wk };
    }, [skus, CUR, PRV, brands, curWeek]);

    /* ---------- inventory lookup ---------- */
    const invFor = (brand, facility, sku) => (inv ? inv.get(`${brand}|${facility}|${String(sku)}`) || null : null);

    async function toggleLoc(facility, sku, facilityId, companySource) {
        // Brand is part of the key: inventory is scoped per company source, so the same
        // SKU at the same facility can have distinct BA vs MS location results.
        const key = `${companySource}|${facility}|${sku}`;
        if (locOpen === key) {
            setLocOpen(null);
            return;
        }
        setLocOpen(key);
        if (locData[key]) return; // cached
        // Prefer the real facility id from the payload; fall back to the static map only
        // if an older payload didn't include one.
        const fid = facilityId || FACID[facility];
        if (!fid) {
            setLocData((prev) => ({ ...prev, [key]: { error: `No facility id known for ${facility}.` } }));
            return;
        }
        if (!companySource) {
            setLocData((prev) => ({ ...prev, [key]: { error: `No brand known for ${facility}.` } }));
            return;
        }
        setLocData((prev) => ({ ...prev, [key]: { loading: true } }));
        const resp = await api.getLocations(fid, sku, companySource);
        if (resp.status === false) {
            setLocData((prev) => ({ ...prev, [key]: { error: resp.msg || 'Could not load locations.' } }));
            return;
        }
        setLocData((prev) => ({ ...prev, [key]: { rows: resp.data?.rows || [] } }));
    }

    /* ---------- SKU lookup model ---------- */
    const lookup = useMemo(() => {
        if (!skus) return null;
        const q = lookupQ.trim().toLowerCase();
        if (q.length < 2) return { needQuery: true };
        const hits = skus.filter((r) => r.name.toLowerCase().includes(q) || r.sku.toLowerCase().includes(q));
        if (!hits.length) return { empty: true, q };
        const bySku = new Map();
        hits.forEach((r) => {
            const e = bySku.get(r.sku) || { sku: r.sku, name: r.name, cat: r.cat, orders: 0, rows: [] };
            e.orders += r.orders;
            e.rows.push(r);
            bySku.set(r.sku, e);
        });
        const list = [...bySku.values()].sort((a, b) => b.orders - a.orders);
        const sel = bySku.has(lookupSel) ? bySku.get(lookupSel) : list[0];
        return { list, sel };
    }, [skus, lookupQ, lookupSel]);

    /* ---------- data-quality callouts ---------- */
    const seenEver = useMemo(() => new Set((all || []).map((r) => `${r.brand}|${r.facility}`)), [all]);
    const absent = useMemo(() => {
        const seenNow = new Set(curRolled.map((e) => `${e.brand}|${e.facility}`));
        return [...seenEver].filter((k) => !seenNow.has(k)).filter((k) => !brands.size || brands.has(k.split('|')[0]));
    }, [seenEver, curRolled, brands]);
    const noData = useMemo(() => curRolled.filter((e) => e.packed > 0 && e.short_ord === 0), [curRolled]);

    /* ---------- day & line model ---------- */
    const dayModel = useMemo(() => {
        if (!days || !days.length) return null;
        const keys = [...new Set(days.map((r) => `${r.brand}|${r.facility}`))].sort();
        const selWeek = curWeek;
        const shortBy = (k) =>
            days
                .filter((r) => `${r.brand}|${r.facility}` === k && (selWeek == null || r.week === selWeek))
                .reduce((s, r) => s + (r.ordersWithShort || 0), 0);
        const worst = [...keys].sort((a, b) => shortBy(b) - shortBy(a))[0];
        const site = keys.includes(daySite) ? daySite : worst || keys[0];
        const [brand, fc] = site.split('|');
        const rows = days.filter(
            (r) => r.brand === brand && r.facility === fc && (selWeek == null || r.week === selWeek),
        );

        const byDay = new Map();
        rows.forEach((r) => {
            const d = String(r.packDate).slice(0, 10);
            const e = byDay.get(d) || {
                d,
                week: r.week,
                packed: 0,
                short: 0,
                lines: 0,
                o0: 0,
                o1: 0,
                o2: 0,
                o3: 0,
                swap: 0,
            };
            e.packed += r.ordersPacked;
            e.short += r.ordersWithShort;
            e.lines += r.lineShorts;
            e.o0 += r.orders0;
            e.o1 += r.orders1;
            e.o2 += r.orders2;
            e.o3 += r.orders3Plus;
            e.swap += r.swapCards;
            byDay.set(d, e);
        });
        const dayArr = [...byDay.values()].sort((a, b) => (a.d < b.d ? -1 : 1));
        const tp = dayArr.reduce((s, e) => s + e.packed, 0);
        const ts = dayArr.reduce((s, e) => s + e.short, 0);

        const byLine = new Map();
        rows.forEach((r) => {
            const l = String(r.packLine);
            const e = byLine.get(l) || { l, packed: 0, short: 0, lines: 0, o3: 0, days: new Set() };
            e.packed += r.ordersPacked;
            e.short += r.ordersWithShort;
            e.lines += r.lineShorts;
            e.o3 += r.orders3Plus;
            e.days.add(String(r.packDate).slice(0, 10));
            byLine.set(l, e);
        });
        const lineArr = [...byLine.values()].sort(
            (a, b) => b.lines / Math.max(b.packed, 1) - a.lines / Math.max(a.packed, 1),
        );

        const dayList = dayArr.map((e) => e.d);
        const dayShort = {};
        dayList.forEach((d) => {
            dayShort[d] = 0;
        });
        days.filter((r) => r.brand === brand && r.facility === fc).forEach((r) => {
            const d = String(r.packDate).slice(0, 10);
            if (d in dayShort) dayShort[d] += r.ordersWithShort;
        });
        const gridItems = new Map();
        (dsku || [])
            .filter((r) => r.brand === brand && r.facility === fc)
            .forEach((r) => {
                const d = String(r.packDate).slice(0, 10);
                if (!(d in dayShort)) return;
                const k = String(r.externalSku);
                const e = gridItems.get(k) || { sku: k, name: r.itemName, total: 0, days: {} };
                e.days[d] = (e.days[d] || 0) + r.ordersAffected;
                e.total += r.ordersAffected;
                gridItems.set(k, e);
            });
        const gridTop = [...gridItems.values()].sort((a, b) => b.total - a.total).slice(0, 15);

        return { keys, site, brand, fc, selWeek, dayArr, tp, ts, lineArr, dayList, dayShort, gridTop };
    }, [days, dsku, daySite, curWeek]);

    /* ---------- live model ---------- */
    const liveModel = useMemo(() => {
        if (!live) return null;
        const field = { 1: 'orders1h', 4: 'orders4h', 24: 'orders24h' }[liveWin];
        let rows = live.filter((r) => (r[field] || 0) > 0);
        if (brands.size) rows = rows.filter((r) => brands.has(r.brand));
        // rank by volume in the chosen window, then by recency
        rows.sort((a, b) => (b[field] || 0) - (a[field] || 0) || (a.minsSinceLast || 0) - (b.minsSinceLast || 0));
        const top = rows.slice(0, 30);
        const active = rows.filter((r) => (r.minsSinceLast || 0) <= 15).length;
        return { field, rows, top, active };
    }, [live, liveWin, brands]);

    // Items driving a specific pack day, for the expandable daily-table row.
    const dayDetailItems = (brand, fc, d, dayShortTotal) => {
        const hits = (dsku || [])
            .filter((r) => r.brand === brand && r.facility === fc && String(r.packDate).slice(0, 10) === d)
            .map((r) => ({ sku: String(r.externalSku), name: String(r.itemName || ''), n: r.ordersAffected }))
            .sort((a, b) => b.n - a.n)
            .slice(0, 12);
        return hits.map((h) => ({
            ...h,
            share: dayShortTotal ? h.n / dayShortTotal : 0,
            catO: CATS[categorise(h.name)],
        }));
    };

    const exportDistributionCsv = () => {
        const header = ['Facility', 'Brand', 'Packed', '0', '1', '2', '3+', 'AnyShort'];
        const lines = [header.join(',')];
        curRolled.forEach((e) => {
            lines.push(
                [e.facility, e.brand.replace('_', ' '), e.packed, e.o0, e.o1, e.o2, e.o3, e.short_ord].join(','),
            );
        });
        downloadText(lines.join('\n'), `bams-shorts-distribution-wk${curWeek || ''}.csv`);
    };

    /* ---------- render ---------- */
    return (
        <div className={`bams-root${dark ? ' dark' : ''}`}>
            <div className="wrap">
                <div className="mast">
                    <div>
                        <h1>Pack Line Shorts</h1>
                        <div className="sub">
                            {curWeek ? `BAMS · week ${curWeek} ${curYear}` : 'Blue Apron / Marley Spoon out-of-stock'}
                        </div>
                    </div>
                    <div className="stamp">
                        <b>{fmt(totals.tp)}</b> orders packed
                        <br />
                        <b>{fmt(totals.ts)}</b> with a short · <b>{fmt(totals.tl)}</b> line shorts
                        <br />
                        <span style={{ color: 'var(--muted)' }}>
                            {pulledAt ? `pulled ${pulledAt.toLocaleTimeString()}` : ''}
                        </span>
                    </div>
                </div>

                <div className="bar">
                    <label className="inline" htmlFor="bams-wk">
                        Week
                    </label>
                    <select
                        id="bams-wk"
                        style={{ minWidth: 150 }}
                        value={selWeekKey || ''}
                        onChange={(e) => setSelWeekKey(Number(e.target.value))}
                    >
                        {weeks.map((k) => (
                            <option key={k} value={k}>
                                Week {k % 100} · {Math.floor(k / 100)}
                            </option>
                        ))}
                    </select>
                    <button type="button" onClick={refresh} disabled={busy}>
                        Refresh
                    </button>
                    <button type="button" className="ghost" onClick={() => setDark((d) => !d)} style={{ minWidth: 74 }}>
                        {dark ? 'Light' : 'Dark'}
                    </button>
                    <span style={{ display: 'flex', gap: 6 }}>
                        {brandList.length > 1 &&
                            brandList.map((b) => (
                                <button
                                    key={b}
                                    type="button"
                                    className="chip"
                                    aria-pressed={!brands.size || brands.has(b)}
                                    onClick={() => toggleBrand(b)}
                                >
                                    {b.replace(/_/g, ' ')}
                                </button>
                            ))}
                    </span>
                </div>

                {status ? (
                    <div className="status">
                        <span className="spin" />
                        {status}
                    </div>
                ) : null}
                {err ? (
                    <div className="errbox">
                        <b>Could not reach the warehouse.</b> {err}
                    </div>
                ) : null}

                {all && !err ? (
                    <>
                        <div className="bar" style={{ marginBottom: 14 }}>
                            <button
                                type="button"
                                className="chip nocaps"
                                aria-pressed={tab === 'week'}
                                onClick={() => switchTab('week')}
                            >
                                Week view
                            </button>
                            <button
                                type="button"
                                className="chip nocaps"
                                aria-pressed={tab === 'day'}
                                onClick={() => switchTab('day')}
                            >
                                Day &amp; line
                            </button>
                            <button
                                type="button"
                                className="chip nocaps"
                                aria-pressed={tab === 'live'}
                                onClick={() => switchTab('live')}
                            >
                                <span className="livedot" />
                                Live
                            </button>
                        </div>

                        {tab === 'week' ? (
                            <WeekView
                                curRolled={curRolled}
                                prvRolled={prvRolled}
                                curWeek={curWeek}
                                priorKey={priorKey}
                                absent={absent}
                                noData={noData}
                                onExportDistribution={exportDistributionCsv}
                                lookup={lookup}
                                lookupQ={lookupQ}
                                setLookupQ={setLookupQ}
                                setLookupSel={setLookupSel}
                                chase={chase}
                                locOpen={locOpen}
                                locData={locData}
                                toggleLoc={toggleLoc}
                                invFor={invFor}
                                trendRef={trendRef}
                                onTrendClick={onTrendClick}
                                trendHtml={trendHtml}
                            />
                        ) : null}

                        {tab === 'day' ? (
                            <DayLineView
                                dayBusy={dayBusy}
                                dayErr={dayErr}
                                dayModel={dayModel}
                                days={days}
                                setDaySite={setDaySite}
                                openDay={openDay}
                                setOpenDay={setOpenDay}
                                dayDetailItems={dayDetailItems}
                                dark={dark}
                            />
                        ) : null}

                        {tab === 'live' ? (
                            <LiveView
                                liveModel={liveModel}
                                liveWin={liveWin}
                                setLiveWin={setLiveWin}
                                liveBusy={liveBusy}
                                liveErr={liveErr}
                                liveAt={liveAt}
                                live={live}
                                onRefresh={refetchLive}
                            />
                        ) : null}
                    </>
                ) : null}
            </div>
        </div>
    );
}
